import React from "react";
import { inr, amountInWords, companyLogoUrl, companySignatureUrl } from "@/lib/erp";
import { resolveDocumentText, resolveDocumentBank } from "@/lib/documentSettings";

// format: "a4" | "a5" | "thermal"
export default function InvoicePrint({ invoice, format = "a4", printArea = true }) {
  const c = invoice.company || {};
  const text = resolveDocumentText(c, invoice, "sales_invoice");
  const bank = resolveDocumentBank(c, invoice);
  const thermal = format === "thermal";
  const twidth = c.thermal_width === "58" ? "58mm" : "80mm";
  const width = format === "a4" ? "210mm" : format === "a5" ? "148mm" : twidth;
  const items = invoice.items || [];
  const customKeys = Array.from(new Set(items.flatMap((it) => Object.keys(it.custom || {})))).filter((k) => items.some((it) => (it.custom || {})[k]));

  const showGstin = c.print_show_gstin !== false;
  const showBank = c.print_show_bank !== false;
  const showTerms = c.print_show_terms !== false;
  const showSign = c.print_show_signature !== false;
  const modern = c.print_template === "modern";
  const footer = c.print_footer_text;
  const logo = c.print_show_logo !== false ? companyLogoUrl(c) : null;
  const signature = showSign ? companySignatureUrl(c) : null;

  if (invoice.import_summary_only) {
    return (
      <div id={printArea ? "print-area" : undefined} data-print-format={format} data-print-wide={format === "thermal" && c.thermal_width === "58" ? "58" : undefined} style={{ width, margin: "0 auto" }}
        className="bg-white text-slate-900 p-6 font-sans text-sm leading-relaxed">
        <div className="font-bold text-lg">{c.name}</div>
        <div className="font-semibold mt-5">HISTORICAL INVOICE SUMMARY — NOT A TAX INVOICE</div>
        <p className="text-xs mt-1">Imported from a summary export. Product lines and GST details were not supplied; this is not a replacement for the original tax invoice.</p>
        <div className="border-t border-slate-300 mt-4 pt-3 space-y-1">
          <div>Invoice: <strong>{invoice.invoice_no}</strong></div>
          <div>Customer: {invoice.customer_name}</div>
          <div>Representative: {invoice.salesperson || "Not provided"}</div>
          <div>Invoice date: {invoice.invoice_date}</div>
          <div>Due date: {invoice.due_date}</div>
        </div>
        <div className="border-t border-slate-300 mt-4 pt-3 space-y-1">
          <div className="flex justify-between"><span>Total</span><strong>{inr(invoice.grand_total)}</strong></div>
          <div className="flex justify-between"><span>Paid</span><span>{inr(invoice.amount_received)}</span></div>
          <div className="flex justify-between"><span>Balance</span><strong>{inr(invoice.balance_due)}</strong></div>
        </div>
        <p className="text-xs mt-4">Payment date and method were not supplied in the export.</p>
      </div>
    );
  }

  if (thermal) return <Thermal invoice={invoice} c={c} width={twidth} showGstin={showGstin} footer={footer} text={text} bank={bank} printArea={printArea} showTerms={showTerms} showBank={showBank} />;

  return (
    <div id={printArea ? "print-area" : undefined} data-print-format={format} data-print-wide={format === "thermal" && c.thermal_width === "58" ? "58" : undefined} style={{ width, margin: "0 auto" }}
      className="bg-white text-slate-900 text-[12px] leading-snug p-6 font-sans box-border">
      {/* Header */}
      <div className={`flex justify-between items-start pb-3 ${modern ? "border-b-4 border-brand-600" : "border-b-2 border-slate-800"}`}>
        <div>
          {logo && <img src={logo} alt="" style={{ maxHeight: "56px", maxWidth: "220px", objectFit: "contain", marginBottom: "6px" }} />}
          <div className={`font-heading text-xl font-bold ${modern ? "text-brand-700" : "text-slate-900"}`}>{c.name}</div>
          <div className="text-slate-600 mt-1 max-w-xs">{c.address}</div>
          {showGstin && <div className="mt-1">GSTIN: <span className="font-mono font-semibold">{c.gstin}</span> · PAN: {c.pan}</div>}
          <div className="text-slate-600">DL No: {c.dl_no}</div>
          <div className="text-slate-600">{c.phone} · {c.email}</div>
        </div>
        <div className="text-right">
          <div className={`inline-block px-3 py-1 font-heading font-bold text-base ${modern ? "bg-brand-600 text-white rounded" : "border border-slate-800"}`}>TAX INVOICE</div>
          <div className="mt-2 font-mono text-sm font-semibold">{invoice.invoice_no}</div>
          {invoice.irn && <div className="text-[10px] text-slate-500 mt-1">IRN: {invoice.irn}</div>}
          {invoice.eway_bill && <div className="text-[10px] text-slate-500">E-Way: {invoice.eway_bill}</div>}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-4 gap-2 border-b border-slate-300 py-2 text-[11px]">
        <Meta label="Invoice Date" value={invoice.invoice_date} />
        <Meta label="Due Date" value={invoice.due_date} />
        <Meta label="Place of Supply" value={`${invoice.place_of_supply_code} — ${invoice.place_of_supply}`} />
        <Meta label="Reverse Charge" value={invoice.reverse_charge ? "Yes" : "No"} />
      </div>

      {/* Parties */}
      <div className="grid grid-cols-2 gap-4 py-3 border-b border-slate-300">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Bill To</div>
          <div className="font-semibold">{invoice.customer_name}</div>
          <div className="text-slate-600">{invoice.billing_address}</div>
          {invoice.customer_gstin && <div>GSTIN: <span className="font-mono">{invoice.customer_gstin}</span></div>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ship To</div>
          <div className="text-slate-600">{invoice.shipping_address || invoice.billing_address}</div>
        </div>
      </div>

      {/* Items */}
      <table className="w-full mt-3 border-collapse text-[11px]">
        <thead>
          <tr className={modern ? "bg-brand-50 text-left" : "bg-slate-100 text-left"}>
            <Th>#</Th><Th>Product</Th><Th>HSN</Th><Th>Batch</Th><Th>Exp</Th>
            {customKeys.map((k) => <Th key={k}>{k}</Th>)}
            <Th right>Qty</Th><Th right>Rate</Th><Th right>Disc%</Th><Th right>GST%</Th><Th right>Amount</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="border-b border-slate-200">
              <Td>{i + 1}</Td>
              <Td><div className="font-medium">{it.name}</div></Td>
              <Td mono>{it.hsn}</Td>
              <Td mono>{it.batch_no}</Td>
              <Td mono>{it.expiry_date}</Td>
              {customKeys.map((k) => <Td key={k}>{(it.custom || {})[k] || ""}</Td>)}
              <Td right>{it.qty}{it.free_qty > 0 && <div className="text-[9px] text-slate-500">Free: {it.free_qty}</div>}</Td>
              <Td right mono>{Number(it.rate).toFixed(2)}</Td>
              <Td right>{it.discount_pct || 0}</Td>
              <Td right>{it.gst_rate}</Td>
              <Td right mono>{Number(it.amount).toFixed(2)}</Td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-between gap-4 mt-3 print-keep">
        <div className="text-[11px] max-w-[55%]">
          <div className="font-semibold">Amount in Words:</div>
          <div className="italic">{amountInWords(invoice.grand_total)}</div>
          {showBank && bank && (
            <div className="mt-3 border border-slate-300 rounded p-2">
              <div className="font-semibold mb-0.5">Bank Details</div>
              <BankFields bank={bank} />
            </div>
          )}
        </div>
        <div className="w-[40%] text-[11px]">
          <PRow label="Taxable Amount" value={invoice.subtotal} />
          {invoice.total_discount > 0 && <PRow label="Discount" value={invoice.total_discount} />}
          {invoice.intra_state ? (<>
            <PRow label="CGST" value={invoice.cgst} />
            <PRow label="SGST" value={invoice.sgst} />
          </>) : <PRow label="IGST" value={invoice.igst} />}
          {invoice.shipping > 0 && <PRow label="Shipping" value={invoice.shipping} />}
          {invoice.other_charges > 0 && <PRow label="Other Charges" value={invoice.other_charges} />}
          {invoice.tcs > 0 && <PRow label="TCS" value={invoice.tcs} />}
          {invoice.tds > 0 && <PRow label="TDS" value={-invoice.tds} />}
          <PRow label="Round Off" value={invoice.round_off} />
          <div className={`flex justify-between pt-1 mt-1 font-bold text-sm ${modern ? "border-t-2 border-brand-600" : "border-t-2 border-slate-800"}`}>
            <span>Grand Total</span><span className="font-mono">{inr(invoice.grand_total)}</span>
          </div>
          <div className="flex justify-between mt-1"><span>Received</span><span className="font-mono">{inr(invoice.amount_received)}</span></div>
          <div className="flex justify-between font-semibold"><span>Balance Due</span><span className="font-mono">{inr(invoice.balance_due)}</span></div>
        </div>
      </div>

      {/* Footer */}
      {(showTerms && (text.notes || text.terms) || showSign) && (
        <div className="document-print-footer grid grid-cols-2 gap-4 mt-6 pt-3 border-t border-slate-300 text-[10px]">
          <div>
            {showTerms && text.notes && <><div className="font-semibold mb-1">Notes</div><div className="text-slate-600 whitespace-pre-wrap">{text.notes}</div></>}
            {showTerms && text.terms && <><div className="font-semibold mb-1 mt-2">Terms &amp; Conditions</div><div className="text-slate-600 whitespace-pre-wrap">{text.terms}</div></>}
          </div>
          {showSign && (
            <div className="document-print-signature text-right flex flex-col items-end min-w-0 max-w-full print-keep">
              <div className="font-semibold max-w-full break-words">For {c.name}</div>
              {signature && (
                <img src={signature} alt="Authorised signature"
                  style={{ maxWidth: "120px", maxHeight: "42px", objectFit: "contain", marginTop: "4px" }} />
              )}
              {(c.signature_name || c.signature_title) && (
                <div className="max-w-full break-words mt-1">
                  {c.signature_name && <div className="font-semibold">{c.signature_name}</div>}
                  {c.signature_title && <div className="text-slate-600">{c.signature_title}</div>}
                </div>
              )}
              <div className={`border-t border-slate-400 pt-1 max-w-full ${signature || c.signature_name || c.signature_title ? "mt-2" : "mt-8"}`}>
                Authorised Signatory
              </div>
            </div>
          )}
        </div>
      )}
      {footer && <div className="text-center text-[10px] text-slate-500 mt-4 pt-2 border-t border-dashed border-slate-300">{footer}</div>}
    </div>
  );
}

function Thermal({ invoice, c, width, showGstin, footer, text, bank, printArea, showTerms, showBank }) {
  const items = invoice.items || [];
  const logo = c.print_show_logo !== false ? companyLogoUrl(c) : null;
  return (
    <div id={printArea ? "print-area" : undefined} data-print-format="thermal" data-print-wide={c.thermal_width === "58" ? "58" : undefined} style={{ width, margin: "0 auto" }}
      className="bg-white text-slate-900 font-mono text-[10px] leading-tight p-2">
      <div className="text-center">
        {logo && <img src={logo} alt="" style={{ maxHeight: "44px", maxWidth: "70%", objectFit: "contain", margin: "0 auto 4px" }} />}
        <div className="font-bold text-[12px]">{c.name}</div>
        <div>{c.address}</div>
        {showGstin && <div>GSTIN: {c.gstin}</div>}
        <div>DL: {c.dl_no}</div>
      </div>
      <div className="border-t border-dashed border-slate-500 my-1" />
      <div>Invoice: {invoice.invoice_no}</div>
      <div>Date: {invoice.invoice_date}</div>
      <div>Customer: {invoice.customer_name}</div>
      <div className="border-t border-dashed border-slate-500 my-1" />
      {items.map((it, i) => (
        <div key={i} className="mb-1">
          <div>{it.name}</div>
          <div className="flex justify-between">
            <span>{it.qty} x {Number(it.rate).toFixed(2)} @{it.gst_rate}%{it.free_qty > 0 ? ` · Free: ${it.free_qty}` : ""}</span>
            <span>{Number(it.amount).toFixed(2)}</span>
          </div>
        </div>
      ))}
      <div className="border-t border-dashed border-slate-500 my-1" />
      <TRow label="Taxable" value={invoice.subtotal} />
      {invoice.intra_state ? (<>
        <TRow label="CGST" value={invoice.cgst} />
        <TRow label="SGST" value={invoice.sgst} />
      </>) : <TRow label="IGST" value={invoice.igst} />}
      <TRow label="Round Off" value={invoice.round_off} />
      <div className="border-t border-dashed border-slate-500 my-1" />
      <div className="flex justify-between font-bold text-[12px]"><span>TOTAL</span><span>{inr(invoice.grand_total)}</span></div>
      <div className="flex justify-between"><span>Received</span><span>{Number(invoice.amount_received).toFixed(2)}</span></div>
       {showBank && bank && <div className="mt-2 text-center"><BankFields bank={bank} /></div>}
       {showTerms && text.notes && <div className="mt-2 whitespace-pre-wrap">Notes: {text.notes}</div>}
       {showTerms && text.terms && <div className="mt-2 whitespace-pre-wrap">Terms: {text.terms}</div>}
      <div className="text-center">{footer || "*** Thank you ***"}</div>
    </div>
  );
}

function BankFields({ bank }) {
  const name = bank.bank_name || bank.name;
  const account = bank.bank_account || bank.account_number || bank.account;
  const ifsc = bank.bank_ifsc || bank.ifsc;
  const fields = [
    name && `Bank: ${name}`,
    account && `A/C: ${account}`,
    ifsc && `IFSC: ${ifsc}`,
    bank.branch_name && `Branch: ${bank.branch_name}`,
    bank.swift_code && `SWIFT: ${bank.swift_code}`,
    bank.upi_id && `UPI: ${bank.upi_id}`,
  ].filter(Boolean);
  return <>{fields.map((field) => <div key={field}>{field}</div>)}</>;
}

const Meta = ({ label, value }) => (<div><div className="text-[9px] uppercase text-slate-500">{label}</div><div className="font-medium">{value}</div></div>);
const Th = ({ children, right }) => <th className={`px-1.5 py-1 border border-slate-300 font-semibold ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, right, mono }) => <td className={`px-1.5 py-1 border border-slate-200 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""}`}>{children}</td>;
const PRow = ({ label, value }) => (<div className="flex justify-between py-0.5"><span className="text-slate-600">{label}</span><span className="font-mono">{Number(value).toFixed(2)}</span></div>);
const TRow = ({ label, value }) => (<div className="flex justify-between"><span>{label}</span><span>{Number(value).toFixed(2)}</span></div>);
